import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { ChartCard, EmptyChart } from '../charts/ChartPrimitives';
import { CHART } from '../../lib/reporting-format';

export type InventoryValueMovement = {
  basis: 'WAC';
  startDate: string;
  endDate: string;
  opening: number;
  received: number;
  cogs: number;
  adjustments: number;
  transfers: number;
  net: number;
  closing: number;
};

interface ValueMovementWaterfallProps {
  loading: boolean;
  data: InventoryValueMovement | undefined;
  /** Range control for this card only — separate from the as-of date. */
  control: React.ReactNode;
}

type WaterfallDatum = {
  label: string;
  base: number; // transparent floor
  delta: number; // visible segment
  signedValue: number; // for the tooltip
  fill: string;
  isTotal: boolean;
};

/**
 * Standard recharts waterfall: a stacked bar with a transparent `base` series
 * carrying each floating bar to its starting height, and a visible `delta`
 * series on top. Opening and Closing are full-height totals; received is a
 * positive (emerald) step, COGS / adjustments are negative (red/amber) steps,
 * transfers signed.
 */
interface WaterfallLabels {
  opening: string;
  received: string;
  cogs: string;
  adjustments: string;
  transfers: string;
  closing: string;
}

function buildWaterfall(d: InventoryValueMovement, labels: WaterfallLabels): WaterfallDatum[] {
  const steps: Array<{ key: string; label: string; value: number; isTotal: boolean }> = [
    { key: 'opening', label: labels.opening, value: d.opening, isTotal: true },
    { key: 'received', label: labels.received, value: d.received, isTotal: false },
    // d.cogs already arrives signed-negative from the RPC. Use it verbatim so a
    // positive-COGS anomaly (data corruption) renders as a visible upward green
    // step instead of being silently flipped down by Math.abs.
    { key: 'cogs', label: labels.cogs, value: d.cogs, isTotal: false },
    { key: 'adjustments', label: labels.adjustments, value: d.adjustments, isTotal: false },
    { key: 'transfers', label: labels.transfers, value: d.transfers, isTotal: false },
    { key: 'closing', label: labels.closing, value: d.closing, isTotal: true },
  ];

  let running = 0;
  const out: WaterfallDatum[] = [];
  for (const step of steps) {
    if (step.isTotal) {
      out.push({
        label: step.label,
        base: 0,
        delta: step.value,
        signedValue: step.value,
        fill: CHART.purple,
        isTotal: true,
      });
      running = step.value;
    } else {
      const start = running;
      const end = running + step.value;
      const base = Math.min(start, end);
      const delta = Math.abs(step.value);
      out.push({
        label: step.label,
        base,
        delta,
        signedValue: step.value,
        fill: step.value >= 0 ? CHART.emerald : CHART.red,
        isTotal: false,
      });
      running = end;
    }
  }
  return out;
}

export const ValueMovementWaterfall: React.FC<ValueMovementWaterfallProps> = ({
  loading,
  data,
  control,
}) => {
  const { t } = useTranslation('inventory');
  const { format, currency } = useCurrencyFormatter();

  const labels: WaterfallLabels = {
    opening: t('reporting.v2.wf.opening', 'Opening'),
    received: t('reporting.v2.wf.received', 'Received'),
    cogs: t('reporting.v2.wf.cogs', 'COGS'),
    adjustments: t('reporting.v2.wf.adjustments', 'Adjustments'),
    transfers: t('reporting.v2.wf.transfers', 'Transfers'),
    closing: t('reporting.v2.wf.closing', 'Closing'),
  };

  const chartData = React.useMemo(
    () => (data ? buildWaterfall(data, labels) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const hasMovement =
    data &&
    (data.opening !== 0 ||
      data.closing !== 0 ||
      data.received !== 0 ||
      data.cogs !== 0 ||
      data.adjustments !== 0 ||
      data.transfers !== 0);

  return (
    <ChartCard
      title={t('reporting.v2.valueMovement', 'Value movement')}
      description={t(
        'reporting.v2.valueMovementDesc',
        'How stock value changed over the selected range. Valued at WAC.',
      )}
      action={control}
    >
      {loading ? (
        <div className="h-80 animate-pulse rounded bg-muted/40" />
      ) : !hasMovement ? (
        <EmptyChart
          message={t('reporting.v2.noValueMovement', 'No value movement in this range.')}
          className="h-80"
        />
      ) : (
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: CHART.axis }}
                tickLine={false}
                axisLine={{ stroke: CHART.grid }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: CHART.axis }}
                tickLine={false}
                axisLine={false}
                width={56}
                tickFormatter={(v: number) => format(v, currency)}
              />
              <Tooltip
                cursor={{ fill: 'rgba(126,105,171,0.06)' }}
                formatter={(_v, _n, item) => {
                  const datum = item?.payload as WaterfallDatum;
                  const signed = datum?.signedValue ?? 0;
                  return [
                    `${signed >= 0 && !datum?.isTotal ? '+' : ''}${format(signed, currency)}`,
                    datum?.label ?? '',
                  ];
                }}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              {/* Transparent floor that lifts each floating bar to its start. */}
              <Bar dataKey="base" stackId="wf" fill="transparent" isAnimationActive={false} />
              <Bar dataKey="delta" stackId="wf" radius={[3, 3, 0, 0]} maxBarSize={56}>
                {chartData.map((entry) => (
                  <Cell key={entry.label} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
};
